import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import useFilters from 'src/registry/hooks/useFilters';
import { useParams } from 'react-router-dom';
import CellSubscriptionItemQuantityEventActions from 'src/features/subscriptionItemQuantityEvents/components/tables/CellSubscriptionItemQuantityEventActions';
import CreateSubscriptionItemQuantityEventFormModal from 'src/features/subscriptionItemQuantityEvents/components/forms/CreateSubscriptionItemQuantityEventFormModal';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { useListSubscriptionItemQuantityEvents } from 'src/features/subscriptionItemQuantityEvents/api/useListSubscriptionItemQuantityEvents';
import { SubscriptionItemQuantityEvent } from 'src/features/subscriptionItemQuantityEvents/utils/types';
import { subItemQuantityEventMessages } from 'src/features/subscriptionItemQuantityEvents/utils/messages';
import { QUANTITY_EVENT_TYPE_BADGE_OPTIONS } from 'src/features/subscriptionItemQuantityEvents/utils/constants';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import React from 'react';
import useSorting from 'src/registry/hooks/useSorting';

const SubscriptionItemQuantityEventsTable = () => {
    const { formatMessage } = useIntl();
    const permissionManageSubscriptions = useHasPermissions([PERMISSION.MANAGE_SUBSCRIPTIONS]);
    const { subscriptionItemId = '' } = useParams();

    const [dateNow] = React.useState(() => Date.now());

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'recorded_at', desc: false }],
    });

    const {
        data: quantityEventsData,
        isLoading,
        isError,
    } = useListSubscriptionItemQuantityEvents({
        subscription_item: subscriptionItemId,
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
    });

    const columnHelper = createColumnHelper<SubscriptionItemQuantityEvent>();

    const columns = [
        columnHelper.accessor('recorded_at', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableColumnFilter: true,
            enableSorting: true,
            meta: {
                title: formatMessage(subItemQuantityEventMessages.recordedAt),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('event_type', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(subItemQuantityEventMessages.eventType),
                badgeOptions: QUANTITY_EVENT_TYPE_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('quantity', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(subItemQuantityEventMessages.quantity),
            },
        }),
        ...(permissionManageSubscriptions
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellSubscriptionItemQuantityEventActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<SubscriptionItemQuantityEvent, unknown>
            columns={columns}
            data={quantityEventsData}
            semantics={{
                title: formatMessage(subItemQuantityEventMessages.quantityEvents),
                entryName: formatMessage(subItemQuantityEventMessages.quantityEventTableBadge).toLowerCase(),
                entryNames: formatMessage(subItemQuantityEventMessages.quantityEventsTableBadge).toLowerCase(),
                placeholder: formatMessage(subItemQuantityEventMessages.quantityEventTableBadge).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageSubscriptions && { newEntry: CreateSubscriptionItemQuantityEventFormModal }),
            }}
        />
    );
};

export default SubscriptionItemQuantityEventsTable;
